import axios from 'axios';

// Create a custom axios instance with longer timeout
const dicomAxios = axios.create({
  timeout: 600000, // 10 minutes timeout
});

export const uploadDicomFile = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('show_pixel_data', true);

  try {
    // Use a CORS proxy for development environment
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    
    // API endpoint
    const apiUrl = 'https://dicomupload.vitalinsights.in/upload';
    
    // Since it was working earlier, let's try the same approach again
    const uploadUrl = isDevelopment ? `${apiUrl}` : apiUrl;
    
    console.log('Uploading DICOM file to:', uploadUrl);
    
    const response = await dicomAxios.post(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // Set withCredentials to false for CORS proxy
      withCredentials: false,
      // Add upload progress tracking
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Cap at 95% to account for server-side processing
          onProgress(Math.min(percentCompleted, 95));
        }
      }
    });

    // Extract relevant data from the response
    const analysisData = response.data.data;
    
    // Find the relevant sections in the response
    let compTrendData = null;
    let compTrendDataPage5 = null;
    // let compTrendDataPage3 = null;
    
    // Look for the data with InstanceNumber 6 and SeriesNumber 3
    analysisData.forEach(data => {
      if (data.SeriesNumber === '3' ) {
        if(data.ImageComments?.SCAN?.COMP_ROI && data.ImageComments?.SCAN?.COMP_ROI.length > 0 && data.ImageComments?.SCAN?.COMP_TREND && data.ImageComments?.SCAN?.COMP_TREND.length > 0) {
          compTrendDataPage5 = data;
        }
        else if(!data.ImageComments?.SCAN?.COMP_ROI && data.ImageComments?.SCAN?.COMP_TREND && data.ImageComments?.SCAN?.COMP_TREND.length > 0) {
          compTrendData = data;
        }
      }                                             

      // if (data.InstanceNumber === '6' && data.SeriesNumber === '3' ) {
      //   compTrendData = data;
      // }                                             
      // else if (data.InstanceNumber === '5' && data.SeriesNumber === '3') {
      //   compTrendDataPage5 = data;
      // }
      // else if (data.InstanceNumber === '3' && data.SeriesNumber === '3') {
      //   compTrendDataPage3 = data;
      // }
      
    });
    
    if (!compTrendData ) {
      throw new Error('Could not find required DICOM data (InstanceNumber 6, SeriesNumber 3)');
    }
    else if (!compTrendDataPage5) {
      throw new Error('Could not find required DICOM data (InstanceNumber 5, SeriesNumber 3)');
    }
    // else if (!compTrendDataPage3) {
    //   throw new Error('Could not find required DICOM data (InstanceNumber 3, SeriesNumber 3)');
    // }
    
    // Helper function to clean numeric values (remove commas and convert to float)
    const cleanNumericValue = (value) => {
      if (typeof value === 'string') {
        return parseFloat(value.replace(/,/g, ''));
      }
      return parseFloat(value);
    };
    
    // Extract COMP_TREND data
    let compTrend;
    if (Array.isArray(compTrendData.ImageComments.SCAN)) {
      const scanWithCompTrend = compTrendData.ImageComments.SCAN.find(scan => scan.COMP_TREND);
      if (scanWithCompTrend) {
        compTrend = scanWithCompTrend.COMP_TREND;
      }
    } else if (compTrendData.ImageComments.SCAN.COMP_TREND) {
      compTrend = compTrendData.ImageComments.SCAN.COMP_TREND;
    }
    // else if(compTrendDataPage3.ImageComments.SCAN.COMP_TREND) {
    //   compTrend = compTrendDataPage3.ImageComments.SCAN.COMP_TREND;
    // }
    
    if (!compTrend || !compTrend[0] || !compTrend[0].EXAM || !compTrend[1] || !compTrend[1].EXAM || !compTrend[2] || !compTrend[2].EXAM) {
      throw new Error('Could not find COMP_TREND data in DICOM file');
    }

    let compTrendPage5;
    if (Array.isArray(compTrendDataPage5.ImageComments.SCAN)) {
      const scanWithCompTrend = compTrendDataPage5.ImageComments.SCAN.find(scan => scan.COMP_TREND);
      if (scanWithCompTrend) {
        compTrendPage5 = scanWithCompTrend.COMP_TREND;
      }
    } else if (compTrendDataPage5.ImageComments.SCAN.COMP_TREND) {
      compTrendPage5 = compTrendDataPage5.ImageComments.SCAN.COMP_TREND;
    }
    
    if (!compTrendPage5 || !compTrendPage5[0] || !compTrendPage5[0].EXAM ) {
      throw new Error('Could not find COMP_TREND data in DICOM file');
    }

    
    const examData = compTrend[0].EXAM;
    const examDataAndrioid = compTrend[1].EXAM;
    const examDataGynoid = compTrend[2].EXAM;
    const examDataBMC = compTrendPage5[0].EXAM;
    
    // Extract patient data
    let patientName = compTrendData.PatientName || '';
    let patientId = compTrendData.PatientID || '';
    let examDate = '';
    // let patientAge = compTrendData.PatientAge ? cleanNumericValue(compTrendData.PatientAge) : null;
    let patientGender = compTrendData.PatientSex || '';
    let height = cleanNumericValue(compTrendData.PatientSize) * 100; // Convert from m to cm
    let weight = cleanNumericValue(compTrendData.PatientWeight); // In kg
    const age = examData.AGE ? cleanNumericValue(examData.AGE) : 0;
    const gender = patientGender === 'F' ? 'Female' : 'Male';
    
    // Extract body composition data
    const fatMass = examData.FAT_MASS ? cleanNumericValue(examData.FAT_MASS) : 0;
    const leanMass = examData.LEAN_MASS ? cleanNumericValue(examData.LEAN_MASS) : 0;
    const totalMass = examData.TOTAL_MASS ? cleanNumericValue(examData.TOTAL_MASS) *1000 : 0;
    let gynoidFat = examDataAndrioid.TISSUE_PFAT ? cleanNumericValue(examDataGynoid.TISSUE_PFAT) : 0;
    let androidFat = examDataGynoid.TISSUE_PFAT ? cleanNumericValue(examDataAndrioid.TISSUE_PFAT) : 0;
    // let androidGynoidRatio = examDataAndrioid.AG_RATIO ? cleanNumericValue(examDataAndrioid.AG_RATIO) : 0;
    let androidGynoidRatio = examDataAndrioid.AG_RATIO ? cleanNumericValue(examDataAndrioid.TISSUE_PFAT) : 0;

    // Calculate body fat percentage if not directly available
    const bodyFatPercentage = examData.TISSUE_PFAT ? 
      cleanNumericValue(examData.TISSUE_PFAT) : 
      (fatMass > 0 && totalMass > 0 ? ((fatMass / (totalMass )) * 100).toFixed(2) : 0);
    
    // Calculate BMC (Bone Mineral Content) if not directly available
    // BMC is typically the difference between total mass and the sum of fat and lean mass
    // First try to get BMC directly from the data
    let bmc = examDataBMC.BMC ? cleanNumericValue(examDataBMC.BMC) : 0;
    // let bmc = null;
    // if (examData.BMC) {
    //   bmc = cleanNumericValue(examData.BMC);
    // } else if (examData.TOTAL_BMC) {
    //   bmc = cleanNumericValue(examData.TOTAL_BMC);
    // } else if (totalMass > 0 && fatMass > 0 && leanMass > 0) {
    //   // If BMC is not directly available, calculate it
    //   // Convert total mass from kg to g for consistent units
    //   bmc = (totalMass * 1000) - fatMass - leanMass;
    // } else {
    //   bmc = 0;
    // }
    
    // // Ensure BMC is not negative due to calculation errors
    // bmc = Math.max(0, bmc);
    
    // Extract raw exam date from DICOM data
    let rawExamDate = '';
    if (compTrendData.ImageComments && compTrendData.ImageComments.EXAM_DATE) {
      rawExamDate = compTrendData.ImageComments.EXAM_DATE;
    } else if (compTrendData.PerformedProcedureStepStartDate) {
      const date = compTrendData.PerformedProcedureStepStartDate;
      rawExamDate = `${date.substring(6, 8)}-${date.substring(4, 6)}-${date.substring(0, 4)}`;
    }

    // Format exam date to YYYY-MM-DD
    if (rawExamDate.includes('-')) {
      const parts = rawExamDate.split('-');
      if (parts.length === 3) {
        examDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        examDate = rawExamDate;
      }
    } else {
      examDate = rawExamDate;
    }
    
    // Extract BMD data from the response
    let bmdSections = [];
    let bmdImages = {};
    let bmdSectionsObj = {
      apSpine: [],
      leftFemur: [],
      rightFemur: []
    };
    
    // Extract BMD images based on specific protocol and instance numbers
    try {
      console.log("Extracting BMD images...");
      let values = [];
      analysisData.forEach(data => {
        if (parseInt(data.SeriesNumber) === 2) {
          if (data.Image && data.Image.s3_url) {
            console.log("BMD image found:", data.InstanceNumber, data.ProtocolName);


            if (data.ProtocolName.includes('AP Spine')) {
                values.push({url: data.Image.s3_url, instanceNumber: data.InstanceNumber});
            }
            console.log("BMD image found:", values);

            // AP Spine - Instance 3
            // if (parseInt(data.InstanceNumber) === 3) {
            //   bmdImages.apSpine = data.Image.s3_url;
            // }
            // // Left Femur - Instance 5
            // else if (parseInt(data.InstanceNumber) === 5) {
            //   bmdImages.rightFemur = data.Image.s3_url;
            // }
            // // Right Femur - Instance 4
            // else if (parseInt(data.InstanceNumber) === 4) {
            //   bmdImages.leftFemur = data.Image.s3_url;
            // }
          }
        }
      });
      
      // Sort values by instanceNumber
      values.sort((a, b) => parseInt(a.instanceNumber) - parseInt(b.instanceNumber));
      
      if (values.length === 3 ) {
        bmdImages.apSpine = values[0].url;
        bmdImages.rightFemur = values[2].url;
        bmdImages.leftFemur = values[1].url;
      }
      else if (values.length === 4) {
        bmdImages.apSpine = values[0].url;
        bmdImages.rightFemur = values[2].url;
        bmdImages.leftFemur = values[1].url;
            }
      
      console.log("Extracted BMD images:", bmdImages);
    } catch (error) {
      console.error("Error extracting BMD images:", error);
    }
    
    try {

      let bmdDataInstance = null;
      // Extract BMD values from Series 3, Instance 3
    // if(compTrendDataPage3.ImageComments.SCAN.COMP_TREND) {
    //   bmdDataInstance = analysisData.find(data => 
    //       data.SeriesNumber === '3' && data.InstanceNumber === '1' && data.ImageComments && data.ImageComments.SCAN);
    // }
    // else{
      //  bmdDataInstance = analysisData.find(data => 
      //   data.SeriesNumber === '3' && data.InstanceNumber === '3' && 
      //   data.ImageComments && data.ImageComments.SCAN && data.ImageComments.SCAN.length > 0 && data.ImageComments.SCAN[0] && data.ImageComments.SCAN[0].ROI && Array.isArray(scanData[0].ROI) && data.ImageComments.SCAN[0].ROI.length === 5);
      
    // }
       bmdDataInstance = analysisData.find(data => 
        data.ImageComments && data.ImageComments.SCAN && data.ImageComments.SCAN.length > 0 
        && data.ImageComments.SCAN[0] && data.ImageComments.SCAN[0].ROI 
        && Array.isArray(data.ImageComments.SCAN[0].ROI) && (data.ImageComments.SCAN[0].ROI.length === 5 || data.ImageComments.SCAN[0].ROI.length === 6 || data.ImageComments.SCAN[0].ROI.length === 4));


      
      if (bmdDataInstance && bmdDataInstance.ImageComments && bmdDataInstance.ImageComments.SCAN) {
        const scanData = bmdDataInstance.ImageComments.SCAN;
        console.log("BMD scanData found in Series", bmdDataInstance.SeriesNumber, "Instance", bmdDataInstance.InstanceNumber);
        
        // Ensure scanData is an array and has sufficient elements
        if (Array.isArray(scanData)) {
          // Log the scanData structure for debugging
          console.log("BMD scanData structure:", JSON.stringify(scanData.map(item => Object.keys(item))));
          
          // Add fallback data to ensure we always have something in bmdSections
          if (bmdSections.length === 0) {
            console.log("Adding fallback BMD data to prevent runtime errors");
            // Add minimal fallback data for each section
            bmdSections.push(
              {
                name: 'AP Spine',
                bmd: 0,
                tscore: 0,
                zscore: 0,
                image: bmdImages['apSpine'] || ''
              },
              {
                name: 'Left Femur',
                bmd: 0,
                tscore: 0,
                zscore: 0,
                image: bmdImages['leftFemur'] || ''
              },
              {
                name: 'Right Femur',
                bmd: 0,
                tscore: 0,
                zscore: 0,
                image: bmdImages['rightFemur'] || ''
              }
            );
          }
          // AP Spine (first item in SCAN array if available)
          if (scanData.length > 0 && scanData[0] && scanData[0].ROI && Array.isArray(scanData[0].ROI)) {
            // Process all AP Spine regions (L1, L2, L3, L4, L1-L4)
            let APSpinlength = scanData[0].ROI.length;
            scanData[0].ROI.forEach((roi, index) => {
              let region = '';
                
              // Map the index to the appropriate region
              if (index === 0) region = 'L1';
              else if (index === 1) region = 'L2';
              else if (index === 2) region = 'L3';
              else if (APSpinlength === 4 && index === 3) region = 'L1-L3';
              else if ((APSpinlength === 5 || APSpinlength === 6) && index === 3) region = 'L4';
              else if ((APSpinlength === 5 || APSpinlength === 6) && index === 4) region = 'L1-L4';
              
              if (index !== 5) {
              bmdSectionsObj.apSpine.push({
                bmd: cleanNumericValue(roi.BMD),
                region: region,
                tScore: cleanNumericValue(roi.BMD_TSCORE),
                zScore: cleanNumericValue(roi.BMD_ZSCORE)
              });
            }
              
              // Also add the L1-L4 total to the main bmdSections array for backward compatibility
              if (((APSpinlength === 5 || APSpinlength === 6) && index === 4) || (APSpinlength === 4 && index === 3)) {
                bmdSections.push({
                  name: 'AP Spine',
                  bmd: cleanNumericValue(roi.BMD),
                  tscore: cleanNumericValue(roi.BMD_TSCORE),
                  zscore: cleanNumericValue(roi.BMD_ZSCORE),
                  image: bmdImages['apSpine'] || ''
                });
              }
            });
          }
          
          // Left Femur (second item in SCAN array if available)
          if (scanData.length > 1 && scanData[1] && scanData[1].ROI && Array.isArray(scanData[1].ROI)) {
            // Process all Left Femur regions
            scanData[1].ROI.forEach((roi, index) => {
              let region = '';
              
              // Map the index to the appropriate region based on the expected format
              // Note: The order in the response is different from what we need in the output
              if (index === 1) region = 'TOTAL LEFT';
              else if (index === 0) region = 'NECK LEFT';
              
              // Create the object with the correct structure
              const femurData = {
                bmd: cleanNumericValue(roi.BMD),
                region: region,
                tScore: roi.BMD_TSCORE,
                zScore: roi.BMD_ZSCORE
              };
              
              // Add to the appropriate array based on region
              if (region === 'NECK LEFT' || region === 'TOTAL LEFT') {
                // Find if this region already exists in the array
                const existingIndex = bmdSectionsObj.leftFemur.findIndex(item => item.region === region);
                if (existingIndex >= 0) {
                  // Update existing entry
                  bmdSectionsObj.leftFemur[existingIndex] = femurData;
                } else {
                  // Add new entry
                  bmdSectionsObj.leftFemur.push(femurData);
                }
              }
              
              // Also add the Total Left to the main bmdSections array for backward compatibility
              if (region === 'TOTAL LEFT') {
                bmdSections.push({
                  name: 'Left Femur',
                  bmd: cleanNumericValue(roi.BMD),
                  tscore: roi.BMD_TSCORE,
                  zscore: roi.BMD_ZSCORE,
                  image: bmdImages['leftFemur'] || ''
                });
              }
            });
            
            // Ensure NECK LEFT comes before TOTAL LEFT in the output
            bmdSectionsObj.leftFemur.sort((a, b) => {
              if (a.region === 'NECK LEFT') return -1;
              if (b.region === 'NECK LEFT') return 1;
              return 0;
            });
          }
          
          // Right Femur (third item in SCAN array if available)
          if (scanData.length > 2 && scanData[2] && scanData[2].ROI && Array.isArray(scanData[2].ROI)) {
            // Process all Right Femur regions
            scanData[2].ROI.forEach((roi, index) => {
              let region = '';
              
              // Map the index to the appropriate region based on the expected format
              // Note: The order in the response is different from what we need in the output
              if (index === 1) region = 'TOTAL RIGHT';
              else if (index === 0) region = 'NECK RIGHT';
              
              // Create the object with the correct structure
              const femurData = {
                bmd: cleanNumericValue(roi.BMD),
                region: region,
                tScore: cleanNumericValue(roi.BMD_TSCORE),
                zScore: cleanNumericValue(roi.BMD_ZSCORE)
              };
              
              // Add to the appropriate array based on region
              if (region === 'NECK RIGHT' || region === 'TOTAL RIGHT') {
                // Find if this region already exists in the array
                const existingIndex = bmdSectionsObj.rightFemur.findIndex(item => item.region === region);
                if (existingIndex >= 0) {
                  // Update existing entry
                  bmdSectionsObj.rightFemur[existingIndex] = femurData;
                } else {
                  // Add new entry
                  bmdSectionsObj.rightFemur.push(femurData);
                }
              }
              
              // Also add the Total Right to the main bmdSections array for backward compatibility
              if (region === 'TOTAL RIGHT') {
                bmdSections.push({
                  name: 'Right Femur',
                  bmd: cleanNumericValue(roi.BMD),
                  tscore: cleanNumericValue(roi.BMD_TSCORE),
                  zscore: cleanNumericValue(roi.BMD_ZSCORE),
                  image: bmdImages['rightFemur'] || ''
                });
              }
            });
            
            // Ensure NECK RIGHT comes before TOTAL RIGHT in the output
            bmdSectionsObj.rightFemur.sort((a, b) => {
              if (a.region === 'NECK RIGHT') return -1;
              if (b.region === 'NECK RIGHT') return 1;
              return 0;
            });
          }
        }
      }
    } catch (error) {
      console.error('Error extracting BMD data:', error);
    }
    
    // Ensure we always have BMD sections data to prevent runtime errors
    if (bmdSections.length === 0) {
      console.log("Adding fallback BMD data outside the main extraction block");
      // Add minimal fallback data for each section
      bmdSections.push(
        {
          name: 'AP Spine',
          bmd: 0,
          tscore: 0,
          zscore: 0,
          image: bmdImages['apSpine'] || ''
        },
        {
          name: 'Left Femur',
          bmd: 0,
          tscore: 0,
          zscore: 0,
          image: bmdImages['leftFemur'] || ''
        },
        {
          name: 'Right Femur',
          bmd: 0,
          tscore: 0,
          zscore: 0,
          image: bmdImages['rightFemur'] || ''
        }
      );
    }
    
    // Use default body asymmetry data as requested
    let bodyAsymmetryData = {
      imageUrl: "",
      limbComposition: {
        trunk: {
          bmc: null,
          fat: null,
          lean: null
        },
        leftArm: {
          bmc: null,
          fat: null,
          lean: null
        },
        leftLeg: {
          bmc: null,
          fat: null,
          lean: null
        },
        rightArm: {
          bmc: null,
          fat: null,
          lean: null
        },
        rightLeg: {
          bmc: null,
          fat: null,
          lean: null
        }
      }
    };

    analysisData.forEach(data => {

      if ((parseInt(data.SeriesNumber) === 2 && parseInt(data.InstanceNumber) === 8) || (parseInt(data.SeriesNumber) === 2 && parseInt(data.InstanceNumber) === 6) || (parseInt(data.SeriesNumber) === 2 && data.ProtocolName === 'Total Body') ) {
        if (data.Image && data.Image.s3_url) {
          console.log("Body Asymmetry image found:", data.InstanceNumber, data.ProtocolName);
          bodyAsymmetryData.imageUrl = data.Image.s3_url;
        }
      } 
      //  if ((parseInt(data.SeriesNumber) === 3 && parseInt(data.InstanceNumber) === 8) || (parseInt(data.SeriesNumber) === 3 && parseInt(data.InstanceNumber) === 6)) {
        if (data.ImageComments && data.ImageComments.SCAN && data.ImageComments.SCAN.COMP_ROI && data.ImageComments.SCAN.COMP_ROI.length ===23) {
          // Parse the limb composition data from COMP_ROI
          const compRoiData = data.ImageComments.SCAN.COMP_ROI;
          
          // Map the data to the existing structure
          // Assuming the data follows a specific order in the array
          // Trunk data (index 0)
          if (compRoiData.length > 0) {
            bodyAsymmetryData.limbComposition.trunk = {
              bmc: cleanNumericValue(compRoiData[8].BMC || 0),
              fat: cleanNumericValue(compRoiData[8].FAT_MASS || 0),
              lean: cleanNumericValue(compRoiData[8].LEAN_MASS || 0)
            };
          }
          
          // Left Arm data (index 1)
          if (compRoiData.length > 1) {
            bodyAsymmetryData.limbComposition.leftArm = {
              bmc: cleanNumericValue(compRoiData[2].BMC || 0),
              fat: cleanNumericValue(compRoiData[2].FAT_MASS || 0),
              lean: cleanNumericValue(compRoiData[2].LEAN_MASS || 0)
            };
          }
          
          // Right Arm data (index 2)
          if (compRoiData.length > 2) {
            bodyAsymmetryData.limbComposition.rightArm = {
              bmc: cleanNumericValue(compRoiData[1].BMC || 0),
              fat: cleanNumericValue(compRoiData[1].FAT_MASS || 0),
              lean: cleanNumericValue(compRoiData[1].LEAN_MASS || 0)
            };
          }
          
          // Left Leg data (index 4)
          if (compRoiData.length > 4) {
            bodyAsymmetryData.limbComposition.leftLeg = {
              bmc: cleanNumericValue(compRoiData[6].BMC || 0),
              fat: cleanNumericValue(compRoiData[6].FAT_MASS || 0),
              lean: cleanNumericValue(compRoiData[6].LEAN_MASS || 0)
            };
          }
          
          // Right Leg data (index 5)
          if (compRoiData.length > 5) {
            bodyAsymmetryData.limbComposition.rightLeg = {
              bmc: cleanNumericValue(compRoiData[5].BMC || 0),
              fat: cleanNumericValue(compRoiData[5].FAT_MASS || 0),
              lean: cleanNumericValue(compRoiData[5].LEAN_MASS || 0)
            };
          }
        }
      // }
    });


    
    // Extract additional data for Fat Analysis section
    let visceralFatArea = null;
    let visceralFatMass = null;
    // let androidGynoidRatio = null;
    // let gynoidFat = null;
    // let androidFat = null;
    let subcutaneousFatMass = null;
    let subcutaneousFatArea = null;
    let fatAnalysisImageUrl = null;
    
    try {
      // First check Series 3, Instance 8 for fat analysis data (primary source)
      const fatAnalysisData8 = analysisData.find(data => 
        (data.SeriesNumber === '3' && data.InstanceNumber === '8'));

        
      
      // Then check Series 3, Instance 6 for fat analysis data (secondary source)
      const fatAnalysisData6 = analysisData.find(data => 
               (!data.ImageComments?.SCAN?.COMP_ROI && data.ImageComments?.SCAN?.COMP_TREND && data.ImageComments?.SCAN?.COMP_TREND.length > 0));

      
      // Finally check Series 3, Instance 3 as fallback
      const fatAnalysisData3 = analysisData.find(data => 
        data.SeriesNumber === '3' && data.InstanceNumber === '3');
      
      
      // Process Instance 8 data (primary source)
      if (fatAnalysisData8 && fatAnalysisData8.ImageComments) {
        console.log("Processing Fat Analysis data from Series 3, Instance 8");
        
        // Check for visceral fat data in ImageComments
        if (fatAnalysisData8.ImageComments.EST_VAT_AREA || fatAnalysisData8.ImageComments.VAT_AREA || fatAnalysisData8.ImageComments.EXAM_VAT_AREA) {
          visceralFatArea = cleanNumericValue(fatAnalysisData8.ImageComments.EST_VAT_AREA || 
                                             fatAnalysisData8.ImageComments.VAT_AREA || 
                                             fatAnalysisData8.ImageComments.EXAM_VAT_AREA);
        }
        
        if (fatAnalysisData8.ImageComments.EST_VAT_MASS || fatAnalysisData8.ImageComments.VAT_MASS || fatAnalysisData8.ImageComments.EXAM_VAT_MASS) {
          visceralFatMass = cleanNumericValue(fatAnalysisData8.ImageComments.EST_VAT_MASS || 
                                            fatAnalysisData8.ImageComments.VAT_MASS || 
                                            fatAnalysisData8.ImageComments.EXAM_VAT_MASS);
        }
        
        // Check for android/gynoid ratio
        // if (fatAnalysisData8.ImageComments.AG_RATIO || fatAnalysisData8.ImageComments.ANDROID_GYNOID_RATIO || fatAnalysisData8.ImageComments.EXAM_AG_RATIO) {
        //   androidGynoidRatio = cleanNumericValue(fatAnalysisData8.ImageComments.AG_RATIO || 
        //                                        fatAnalysisData8.ImageComments.ANDROID_GYNOID_RATIO || 
        //                                        fatAnalysisData8.ImageComments.EXAM_AG_RATIO);
        // }
        
        // // Check for android and gynoid fat percentages
        // if (fatAnalysisData8.ImageComments.ANDROID_FAT || fatAnalysisData8.ImageComments.EXAM_ANDROID_FAT) {
        //   androidFat = cleanNumericValue(fatAnalysisData8.ImageComments.ANDROID_FAT || 
        //                                 fatAnalysisData8.ImageComments.EXAM_ANDROID_FAT);
        // }
        
        // if (fatAnalysisData8.ImageComments.GYNOID_FAT || fatAnalysisData8.ImageComments.EXAM_GYNOID_FAT) {
        //   gynoidFat = cleanNumericValue(fatAnalysisData8.ImageComments.GYNOID_FAT || 
        //                                fatAnalysisData8.ImageComments.EXAM_GYNOID_FAT);
        // }
        
        // Check for subcutaneous fat data
        if (fatAnalysisData8.ImageComments.SAT_AREA || fatAnalysisData8.ImageComments.EXAM_SAT_AREA) {
          subcutaneousFatArea = cleanNumericValue(fatAnalysisData8.ImageComments.SAT_AREA || 
                                                fatAnalysisData8.ImageComments.EXAM_SAT_AREA);
        }
        
        if (fatAnalysisData8.ImageComments.SAT_MASS || fatAnalysisData8.ImageComments.EXAM_SAT_MASS) {
          subcutaneousFatMass = cleanNumericValue(fatAnalysisData8.ImageComments.SAT_MASS || 
                                                fatAnalysisData8.ImageComments.EXAM_SAT_MASS);
        }
        
        // Check for nested SCAN structure
        if (fatAnalysisData8.ImageComments.SCAN) {
          const scan = fatAnalysisData8.ImageComments.SCAN;
          
          // If SCAN is an array, process each item
          if (Array.isArray(scan)) {
            scan.forEach(scanItem => {
              if (scanItem.COMP_TREND) {
                processFatAnalysisCompTrend(scanItem.COMP_TREND);
              }
            });
          } 
          // If SCAN is an object with COMP_TREND
          else if (scan.COMP_TREND) {
            processFatAnalysisCompTrend(scan.COMP_TREND);
          }
        }
        
        // Get the fat analysis image if available
        if (fatAnalysisData8.Image && fatAnalysisData8.Image.s3_url) {
          fatAnalysisImageUrl = fatAnalysisData8.Image.s3_url;
        }
      }
      
      // Process Instance 6 data if needed (secondary source)
      if ((!visceralFatArea || !visceralFatMass || !androidGynoidRatio || !gynoidFat || !androidFat) && 
          fatAnalysisData6 && fatAnalysisData6.ImageComments) {
        console.log("Processing Fat Analysis data from Series 3, Instance 6");
        
        // Check for visceral fat data
        if (!visceralFatArea && (fatAnalysisData6.ImageComments.EST_VAT_AREA || fatAnalysisData6.ImageComments.VAT_AREA || fatAnalysisData6.ImageComments.EXAM_VAT_AREA)) {
          visceralFatArea = cleanNumericValue(fatAnalysisData6.ImageComments.EST_VAT_AREA || 
                                             fatAnalysisData6.ImageComments.VAT_AREA || 
                                             fatAnalysisData6.ImageComments.EXAM_VAT_AREA);
        }
        
        if (!visceralFatMass && (fatAnalysisData6.ImageComments.EST_VAT_MASS || fatAnalysisData6.ImageComments.VAT_MASS || fatAnalysisData6.ImageComments.EXAM_VAT_MASS)) {
          visceralFatMass = cleanNumericValue(fatAnalysisData6.ImageComments.EST_VAT_MASS || 
                                            fatAnalysisData6.ImageComments.VAT_MASS || 
                                            fatAnalysisData6.ImageComments.EXAM_VAT_MASS);
        }
        
        // Check for android/gynoid ratio
        if (!androidGynoidRatio && (fatAnalysisData6.ImageComments.AG_RATIO || fatAnalysisData6.ImageComments.ANDROID_GYNOID_RATIO || fatAnalysisData6.ImageComments.EXAM_AG_RATIO)) {
          androidGynoidRatio = cleanNumericValue(fatAnalysisData6.ImageComments.AG_RATIO || 
                                               fatAnalysisData6.ImageComments.ANDROID_GYNOID_RATIO || 
                                               fatAnalysisData6.ImageComments.EXAM_AG_RATIO);
        }
        
        // Check for android and gynoid fat percentages
        if (!androidFat && (fatAnalysisData6.ImageComments.ANDROID_FAT || fatAnalysisData6.ImageComments.EXAM_ANDROID_FAT)) {
          androidFat = cleanNumericValue(fatAnalysisData6.ImageComments.ANDROID_FAT || 
                                        fatAnalysisData6.ImageComments.EXAM_ANDROID_FAT);
        }
        
        if (!gynoidFat && (fatAnalysisData6.ImageComments.GYNOID_FAT || fatAnalysisData6.ImageComments.EXAM_GYNOID_FAT)) {
          gynoidFat = cleanNumericValue(fatAnalysisData6.ImageComments.GYNOID_FAT || 
                                       fatAnalysisData6.ImageComments.EXAM_GYNOID_FAT);
        }
        
        // Check for nested SCAN structure
        if (fatAnalysisData6.ImageComments.SCAN) {
          const scan = fatAnalysisData6.ImageComments.SCAN;
          
          // If SCAN is an array, process each item
          if (Array.isArray(scan)) {
            scan.forEach(scanItem => {
              if (scanItem.COMP_TREND) {
                processFatAnalysisCompTrend(scanItem.COMP_TREND);
              }
            });
          } 
          // If SCAN is an object with COMP_TREND
          else if (scan.COMP_TREND) {
            processFatAnalysisCompTrend(scan.COMP_TREND);
          }
        }
        
        // Get the fat analysis image if not already found
        if (!fatAnalysisImageUrl && fatAnalysisData6.Image && fatAnalysisData6.Image.s3_url) {
          fatAnalysisImageUrl = fatAnalysisData6.Image.s3_url;
        }
      }
      
      // Process Instance 3 data as fallback
      if ((!visceralFatArea || !visceralFatMass || !androidGynoidRatio || !gynoidFat || !androidFat) && 
          fatAnalysisData3 && fatAnalysisData3.ImageComments) {
        console.log("Processing Fat Analysis data from Series 3, Instance 3 (fallback)");
        
        // Check for visceral fat data
       

        if (!visceralFatArea && (fatAnalysisData3.ImageComments.EST_VAT_AREA || fatAnalysisData3.ImageComments.VAT_AREA || fatAnalysisData3.ImageComments.EXAM_VAT_AREA)) {
          visceralFatArea = cleanNumericValue(fatAnalysisData3.ImageComments.EST_VAT_AREA || 
                                             fatAnalysisData3.ImageComments.VAT_AREA || 
                                             fatAnalysisData3.ImageComments.EXAM_VAT_AREA);
        }
        
        if (!visceralFatMass && (fatAnalysisData3.ImageComments.EST_VAT_MASS || fatAnalysisData3.ImageComments.VAT_MASS || fatAnalysisData3.ImageComments.EXAM_VAT_MASS)) {
          visceralFatMass = cleanNumericValue(fatAnalysisData3.ImageComments.EST_VAT_MASS || 
                                            fatAnalysisData3.ImageComments.VAT_MASS || 
                                            fatAnalysisData3.ImageComments.EXAM_VAT_MASS);
        }

        
        // Check for android/gynoid ratio
        if (!androidGynoidRatio && fatAnalysisData3.ImageComments.EXAM_AG_RATIO) {
          androidGynoidRatio = cleanNumericValue(fatAnalysisData3.ImageComments.EXAM_AG_RATIO);
        }
        
        // Check for android and gynoid fat percentages
        if (!androidFat && fatAnalysisData3.ImageComments.EXAM_ANDROID_FAT) {
          androidFat = cleanNumericValue(fatAnalysisData3.ImageComments.EXAM_ANDROID_FAT);
        }
        
        if (!gynoidFat && fatAnalysisData3.ImageComments.EXAM_GYNOID_FAT) {
          gynoidFat = cleanNumericValue(fatAnalysisData3.ImageComments.EXAM_GYNOID_FAT);
        }
        
        // Get the fat analysis image if not already found
        if (!fatAnalysisImageUrl && fatAnalysisData3.Image && fatAnalysisData3.Image.s3_url) {
          fatAnalysisImageUrl = fatAnalysisData3.Image.s3_url;
        }
      }

      
      // Helper function to process COMP_TREND data for fat analysis
      function processFatAnalysisCompTrend(compTrend) {
        if (!Array.isArray(compTrend)) return;
        
        // Look for EST_VAT data
        const vatData = compTrend.find(item => item.EXAM && 
          (item.EXAM.EST_VAT_AREA || item.EXAM.VAT_AREA || item.EXAM.EXAM_VAT_AREA));
        
        if (vatData && vatData.EXAM) {
          if (!visceralFatArea) {
            visceralFatArea = cleanNumericValue(vatData.EXAM.EST_VAT_AREA || 
                                               vatData.EXAM.VAT_AREA || 
                                               vatData.EXAM.EXAM_VAT_AREA);
          }
          
          if (!visceralFatMass) {
            visceralFatMass = cleanNumericValue(vatData.EXAM.EST_VAT_MASS || 
                                              vatData.EXAM.VAT_MASS || 
                                              vatData.EXAM.EXAM_VAT_MASS);
          }
        }
      }
      
    } catch (error) {
      console.error('Error extracting body asymmetry data:', error);
    }
    
    // Extract institution information
    const institutionName = compTrendData.InstitutionName || '';
    const institutionAddress = compTrendData.InstitutionAddress || '';
    const institutionalDepartmentName = compTrendData.InstitutionalDepartmentName || '';
    const manufacturer = compTrendData.Manufacturer || '';
    const deviceModel = compTrendData.ManufacturerModelName || '';
    const deviceSerialNumber = compTrendData.DeviceSerialNumber || '';
    
    // Extract patient birth date and format it
    let dob = null;
    if (compTrendData.PatientBirthDate) {
      const birthDate = compTrendData.PatientBirthDate;
      if (birthDate.length === 8) {
        // Format from YYYYMMDD to YYYY-MM-DD
        dob = `${birthDate.substring(0, 4)}-${birthDate.substring(4, 6)}-${birthDate.substring(6, 8)}`;
      }
    }
    
    return {
      // Patient demographics
      patientName,
      patientId,
      patientSize: height,
      patientWeight: weight,
      patientAge: age,
      patientSex: gender,
      patientDob: dob,
      dateOfExam: examDate,
      
      // Body composition data
      fatMass,
      leanMass,
      totalMass,
      bodyFatPercentage,
      bmc,
      
      // BMD data with detailed regions
      bmdSections: bmdSectionsObj,  // Keep this for backward compatibility // Use bmdSectionsObj as the primary structure
      bmdImages,
      
      // Fat Analysis data
      visceralFatArea,
      visceralFatMass: visceralFatMass,
      androidGynoidRatio,
      gynoidFat,
      androidFat,
      subcutaneousFatMass,
      subcutaneousFatArea,
      fatAnalysisImageUrl,
      
      // Body Asymmetry data in the new format
      bodyAsymmetryData,
      
      // Institution and device info
      institutionName,
      institutionAddress,
      institutionalDepartmentName,
      manufacturer,
      deviceModel,
      deviceSerialNumber
    };
  } catch (error) {
    console.error('Error uploading DICOM file:', error);
    throw new Error('Failed to upload DICOM file: ' + error.message);
  }
};
